import { useCallback, useEffect, useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { PromptsTable } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/prompts-table/PromptsTable";
import { TopicsTable } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/topics-table/TopicsTable";
import { PromptsCounter } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/PromptsCounter";

import type { IModifiablePrompt, IModifiableTopic } from "./types";

interface IPromptsEditModalProps {
    isOpen: boolean;
    topics: IModifiableTopic[];
    isLoading: boolean;
    isSaveEnabled: boolean;
    isSaving?: boolean;
    onClose: () => void;
    onApprove: () => void;
    onDeletePrompts: (prompts: IModifiablePrompt[]) => void;
    onEditPrompts: (prompts: IModifiablePrompt[], newTopic: string) => void;
    onCreatePrompt: (text: string, topic: string) => void;
    onCreateTopic: (name: string) => void;
    onEditTopic: (oldName: string, newName: string) => void;
    onDeleteTopic: (name: string) => void;
    onCSVUpload: (files: File[]) => Promise<void>;
    onGeneratePrompts?: (topic: string, count: number) => Promise<void>;
    onManageLaterClick?: () => void;
    saveText: string;
    error?: boolean;
}

export const PromptsEditModal = ({
    isOpen,
    topics,
    isLoading,
    isSaveEnabled,
    isSaving = false,
    onClose,
    onApprove,
    onDeletePrompts,
    onEditPrompts,
    onCreatePrompt,
    onCreateTopic,
    onGeneratePrompts,
    onEditTopic,
    onDeleteTopic,
    onCSVUpload,
    onManageLaterClick,
    saveText,
    error,
}: IPromptsEditModalProps) => {
    const [selectedTopic, setSelectedTopic] = useState<string>(ALL_TOPICS_NAME);
    const actualTopics = useMemo<IModifiableTopic[]>(
        () => topics.filter(({ topicId }) => topicId !== ALL_TOPICS_NAME),
        [topics],
    );

    const totalPromptsCount = useMemo<number>(
        () => actualTopics.reduce((acc, { prompts }) => acc + (prompts?.length ?? 0), 0),
        [actualTopics],
    );

    useEffect(() => {
        setSelectedTopic(ALL_TOPICS_NAME);
    }, [isOpen]);

    const onTopicCreation = useCallback(
        (name: string) => {
            setSelectedTopic(name);
            onCreateTopic?.(name);
        },
        [setSelectedTopic, onCreateTopic],
    );

    const onTopicNameChange = useCallback(
        (oldName: string, newName: string): void => {
            if (selectedTopic === oldName) {
                setSelectedTopic(newName);
            }

            onEditTopic?.(oldName, newName);
        },
        [selectedTopic, setSelectedTopic, onEditTopic],
    );

    const onTopicDeletion = useCallback(
        (topic: string): void => {
            if (selectedTopic === topic) {
                setSelectedTopic(ALL_TOPICS_NAME);
            }

            onDeleteTopic?.(topic);
        },
        [selectedTopic, setSelectedTopic, onDeleteTopic],
    );

    return (
        <Modal
            isOpen={isOpen}
            isFullScreen
            onRequestClose={onClose}
            shouldCloseOnEsc={false}
        >
            <Modal.Header>
                <Flex
                    justifyContent="space-between"
                    alignItems="flex-end"
                    width="100%"
                >
                    <Flex
                        flexDirection="column"
                        gap="4px"
                    >
                        <Modal.Title>{translate("llm.prompts.edit.title")}</Modal.Title>
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate("llm.prompts.edit.subtitle")}
                        </Text>
                    </Flex>
                    <PromptsCounter totalPrompts={totalPromptsCount} />
                </Flex>
            </Modal.Header>
            <Modal.Content height="100%">
                <Flex
                    height="100%"
                    margin="0 0 8px 0"
                    border={`1px solid ${colorsPalettes.carbon[100]}`}
                    borderRadius="6px"
                    overflow="hidden"
                >
                    <TopicsTable
                        topics={topics}
                        isLoading={isLoading}
                        selectedTopic={selectedTopic}
                        totalPromptsCount={totalPromptsCount}
                        onTopicSelection={setSelectedTopic}
                        onTopicNameChange={onTopicNameChange}
                        onTopicCreation={onTopicCreation}
                        onTopicDeletion={onTopicDeletion}
                        error={error}
                    />
                    <PromptsTable
                        key={`${selectedTopic}-${topics.length}`}
                        allTopics={topics}
                        actualTopics={actualTopics}
                        totalPromptsCount={totalPromptsCount}
                        selectedTopic={selectedTopic}
                        isLoading={isLoading}
                        error={Boolean(error)}
                        onDeletePrompts={onDeletePrompts}
                        onEditPrompts={onEditPrompts}
                        onCreatePrompt={onCreatePrompt}
                        onGeneratePrompts={onGeneratePrompts}
                        onCSVUpload={onCSVUpload}
                        onManageLaterClick={onManageLaterClick}
                    />
                </Flex>
            </Modal.Content>
            <Modal.Footer withBorder>
                <Button
                    data-automation="prompts-main-modal-cancel"
                    type="flat"
                    onClick={onClose}
                    isDisabled={isSaving}
                >
                    {translate("global.cancel")}
                </Button>
                <Button
                    data-automation="prompts-main-modal-save"
                    type="primary"
                    onClick={onApprove}
                    isLoading={isSaveEnabled ? isLoading : undefined}
                    isDisabled={!isSaveEnabled}
                >
                    {saveText}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
